import React, { useState } from 'react';
import { Button, Modal, Select, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowsExchange } from '@tabler/icons-react';
import useSWR, { useSWRConfig } from 'swr';
import { apiErrorMessage } from '../data/apiClient';
import { exitContext, listSwitchableOrgs, switchOrg } from '../data/Context';
import { useAuth } from '../hooks/useAuth';
import { useCsrf } from '../hooks/useCsrf';

/**
 * Watchly-staff-only control to switch the session's active org context into a
 * customer org (or back out). Not rendered for regular users.
 */
const OrgSwitcher: React.FC = () => {
    const { isStaff, activeOrg, organization } = useAuth();
    const { csrfToken } = useCsrf();
    const { mutate: globalMutate } = useSWRConfig();
    const [opened, { open, close }] = useDisclosure(false);
    const [busy, setBusy] = useState(false);

    const { data: orgs } = useSWR(
        isStaff && opened ? 'api/organizations' : null,
        listSwitchableOrgs,
    );

    if (!isStaff) return null;

    // Whether we're currently viewing a customer (not our own home org).
    const isSwitched = !!activeOrg && !!organization && activeOrg.id !== organization.id;

    const applyContextChange = async (fn: () => Promise<void>, message: string) => {
        setBusy(true);
        try {
            await fn();
            // Context changed: refetch everything (incl. /me) so the whole app
            // reflects the newly-active org.
            await globalMutate(() => true, undefined, { revalidate: true });
            notifications.show({ message });
            close();
        } catch (err: any) {
            notifications.show({
                color: 'red',
                title: 'Could not switch context',
                message: apiErrorMessage(err),
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Button
                variant={isSwitched ? 'filled' : 'light'}
                color={isSwitched ? 'orange' : undefined}
                size="xs"
                leftSection={<IconArrowsExchange size={16} />}
                onClick={open}
            >
                {isSwitched ? `Viewing: ${activeOrg?.name}` : 'Switch customer'}
            </Button>

            <Modal opened={opened} onClose={close} title="Switch customer" centered>
                <Stack>
                    <Text size="sm" c="dimmed">
                        Act within a customer’s organization. You remain signed in as yourself;
                        this only changes which org you’re viewing.
                    </Text>
                    <Select
                        placeholder="Choose an organization…"
                        searchable
                        disabled={busy}
                        data={(orgs ?? []).map(org => ({ value: org.id, label: org.name }))}
                        value={isSwitched ? activeOrg?.id ?? null : null}
                        onChange={orgId =>
                            orgId &&
                            applyContextChange(
                                () => switchOrg(csrfToken, orgId),
                                'Switched customer context',
                            )
                        }
                    />
                    {isSwitched && (
                        <Button
                            variant="subtle"
                            color="gray"
                            loading={busy}
                            onClick={() =>
                                applyContextChange(
                                    () => exitContext(csrfToken),
                                    'Returned to your organization',
                                )
                            }
                        >
                            Exit customer view
                        </Button>
                    )}
                </Stack>
            </Modal>
        </>
    );
};

export default OrgSwitcher;
